import moment from 'moment';
import { useRef, useState } from 'react';
import styled from 'styled-components';
import { SecondaryButton } from 'ui/button';
import { ChevronDownSmall } from 'ui/icons';
import { DateRangePicker } from 'ui/datePicker';
import { RELATIVE_DATE_PRESETS_WITH_ALL_TIME } from 'ui/datePicker/helpers';
import { displayDate } from 'utils/date';
import { useClickAway } from 'react-use';
import { useInsightsQueryContext } from './contexts';
import { useTranslation } from '../hooks';

const Wrapper = styled.div`
  position: relative;
  z-index: 10000;
`;

const ContentWrapper = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background-color: #fff;
  border-radius: ${({ theme }) => theme.spacing.values.half};
  margin-top: ${({ theme }) => theme.spacing.values.half};
`;

const Trigger = styled(SecondaryButton)`
  display: flex;
  align-items: center;
  column-gap: ${({ theme }) => theme.spacing.values.quarter};

  & svg {
    color: #fcfcfc;
    flex-shrink: 0;
  }
`;

const useDisplayDateRange = () => {
  const { startDate, endDate } = useInsightsQueryContext();
  const { t } = useTranslation();
  const displayStartDate = displayDate(startDate);
  const displayEndDate = displayDate(endDate);

  if (startDate && endDate) {
    return t('insights.dateRange', {
      startDate: displayStartDate,
      endDate: displayEndDate,
    });
  }

  if (startDate) {
    return t('insights.afterDate', { startDate: displayStartDate });
  }

  if (endDate) {
    return t('insights.beforeDate', { endDate: displayEndDate });
  }

  return t('insights.allTime');
};

export const InsightsTimeframePicker = () => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { startDate, endDate, setStartDate, setEndDate } = useInsightsQueryContext();
  const [isExpanded, setIsExpanded] = useState(false);
  useClickAway(wrapperRef, () => {
    setIsExpanded(false);
  });
  const displayDateRange = useDisplayDateRange();

  return (
    <Wrapper ref={wrapperRef}>
      <Trigger
        onClick={() => setIsExpanded((prevIsExpanded) => !prevIsExpanded)}
        data-test="insights-timeframe-picker-trigger"
      >
        {displayDateRange}
        <ChevronDownSmall isColorInherited />
      </Trigger>
      {isExpanded && (
        <ContentWrapper data-test="data-range-picker">
          <DateRangePicker
            startDate={startDate ? moment(startDate) : null}
            endDate={endDate ? moment(endDate) : null}
            onDateRangeChange={(newRange) => {
              setStartDate(newRange.startDate);
              setEndDate(newRange.endDate);
            }}
            presets={RELATIVE_DATE_PRESETS_WITH_ALL_TIME}
            showRelativeDatePresets
            blockFutureDates
            hasAllTimePreset
          />
        </ContentWrapper>
      )}
    </Wrapper>
  );
};
